import type {
  ApplicantCount,
  JobDisplayTag,
} from '@seek/ca-graphql-schema/types';
import { JOB_LISTING_NUMBER_OF_SELLING_POINT_KEY } from '@seek/job-details-view';
import { useCallback } from 'react';

import { useSplitViewContext } from 'src/handlers/SplitViewContext';
import {
  getIsAddSalaryPrefNudgeShown,
  getIsSalaryMatch,
} from 'src/hooks/trackJobDetailsLoaded';
import { useSolHash } from 'src/hooks/useSolHash';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { useEnableNumberOfApplicants } from 'src/store/featureFlags/hooks';
import type {
  JobDetails,
  PersonalisedJobDetails,
} from 'src/store/jobdetails/types';
import { useSelector } from 'src/store/react';
import {
  selectAuthenticated,
  selectSearchQuery,
  selectSolMetadata,
} from 'src/store/selectors';
import { getViewJobOriginRef } from 'src/utils/eventCapture/eventCaptureUtils';
import { mapDisplayTagsType } from 'src/utils/mapJobTagsType';
import { getParam } from 'src/utils/urlParams';

import { useSourcrRecruiterForTracking } from '../SearchResultPage/SearchResultList/JobListItem/utils/utils';

interface Options {
  jobDetails: JobDetails;
  isHirerVerified: boolean;
  jobDetailsPersonalised?: PersonalisedJobDetails;
  solMetadataReference?: string;
  isMatchedSkillsWidgetShown?: boolean;
  isMatchedSkillsNudgeShown?: boolean;
  jobOrigin?: string;
  jobSellingPoints?: number;
}

type UseApplyTracking = (options: Options) => [string, () => void];

const useJdvBadgesTracking = ({
  hasTopApplicantBadge,
  displayTags,
}: {
  hasTopApplicantBadge: boolean;
  displayTags?: JobDisplayTag[];
}) => {
  const topApplicantBadge: Array<'TOP_APPLICANT'> = hasTopApplicantBadge
    ? ['TOP_APPLICANT']
    : [];
  return [...topApplicantBadge, ...mapDisplayTagsType(displayTags)];
};

const useSourcrOnJobCardTracking = () => {
  // Sourcr recruiter only appears on SplitView job cards currently
  const selectedJob = useSplitViewContext()?.selectedJob;

  return useSourcrRecruiterForTracking(selectedJob);
};

export const useApplyTracking: UseApplyTracking = ({
  jobDetails,
  jobDetailsPersonalised,
  solMetadataReference,
  isHirerVerified,
  isMatchedSkillsWidgetShown,
  isMatchedSkillsNudgeShown,
  jobOrigin,
  jobSellingPoints,
}) => {
  const analyticsFacade = useAnalyticsFacade();
  const isAuthenticated = useSelector(selectAuthenticated);
  const solMetadata = useSelector(selectSolMetadata);
  const searchParams = useSelector(selectSearchQuery);

  const { sourcr } = jobDetails;
  const { id, isLinkOut, salary, products } = jobDetails?.job || {};
  const [urlStr, solMeta] = useSolHash({
    id,
    solMetadataReference,
  });

  const salaryMatch = jobDetailsPersonalised?.salaryMatch;
  const isSalaryMatch = getIsSalaryMatch(salaryMatch);
  const searchRemoteOptions = searchParams?.workarrangement;
  const isAddSalaryPrefNudgeShown = getIsAddSalaryPrefNudgeShown({
    isAuthenticated,
    isSalaryLabel: Boolean(salary?.label),
    salaryMatch,
  });

  const isNumberOfApplicantsEnabled =
    useEnableNumberOfApplicants() && isAuthenticated;

  const jdvDisplayTags = useJdvBadgesTracking({
    hasTopApplicantBadge: Boolean(jobDetailsPersonalised?.topApplicantBadge2),
    displayTags: jobDetails?.job?.products?.displayTags,
  });
  const sourcrRecruiterOnJobCard = useSourcrOnJobCardTracking();

  const experimentFlightId = solMetadata?.tags?.mordor__flights ?? '';

  const onApplyClick = useCallback(() => {
    /**
     * If there is no ref query in the URL and the user is
     * in splitView then it is guaranteed the user clicked
     * apply from split view SERP.
     */
    const { count: jobApplicantsVolume } = isNumberOfApplicantsEnabled
      ? ((jobDetails?.insights?.[0] as ApplicantCount) ?? {})
      : {};

    const viewJobOriginRef = getViewJobOriginRef();
    const sellingPointsOrigin = getParam(
      JOB_LISTING_NUMBER_OF_SELLING_POINT_KEY,
    );
    const sellingPoints =
      jobSellingPoints ??
      (sellingPointsOrigin ? Number(sellingPointsOrigin) : undefined);
    const ref = jobOrigin || viewJobOriginRef || 'search-split';

    analyticsFacade.jobApplyClicked(
      {
        experimentFlightId,
        href: urlStr,
        jobBadges: jdvDisplayTags,
        jobApplicantsVolume,
        jobViewOriginQuery: ref,
        isHirerVerified,
        isMatchedSkillsWidgetShown,
        isMatchedSkillsNudgeShown,
        jobBadgesFilter: searchParams?.tags ? [searchParams?.tags] : [],
        jobCardSellPointCount: sellingPoints ?? products?.bullets?.length,
        hasSourcr: Boolean(sourcr),
      },
      {
        isAddSalaryPrefNudgeShown,
        isSalaryMatch,
        searchRemoteOptions,
        jobBadges: jdvDisplayTags,
        jobId: id,
        linkout: isLinkOut,
        solMetadata: solMeta,
        thirdPartyLink: isLinkOut,
        isMatchedSkillsWidgetShown,
        isMatchedSkillsNudgeShown,
        ...(sourcrRecruiterOnJobCard
          ? { recruiterId: sourcrRecruiterOnJobCard.id }
          : {}),
      },
    );
  }, [
    analyticsFacade,
    experimentFlightId,
    id,
    isAddSalaryPrefNudgeShown,
    isHirerVerified,
    isLinkOut,
    isMatchedSkillsNudgeShown,
    isMatchedSkillsWidgetShown,
    isNumberOfApplicantsEnabled,
    isSalaryMatch,
    jdvDisplayTags,
    jobDetails?.insights,
    jobOrigin,
    jobSellingPoints,
    products?.bullets?.length,
    searchParams?.tags,
    searchRemoteOptions,
    solMeta,
    sourcr,
    sourcrRecruiterOnJobCard,
    urlStr,
  ]);

  return [urlStr, onApplyClick];
};
